import { Group, Select, Stack, Switch, TextInput } from '@mantine/core'
import {
  DatePickerInput,
  MonthPickerInput,
  YearPickerInput,
} from '@mantine/dates'
import { Suspense, useMemo } from 'react'
import { z } from 'zod'
import { useMetrics } from '../../../../widgets/api'
import { useAllPlayers } from '../../../../players/api'
import { useAllTeams } from '../../../../teams/api'
import { useDivisions } from '../../../../ladder/api'
import { useCompetitionRounds, useMatches } from '../../../../matches/api'
import { QuarterPickerInput } from '../../../../widgets/components/QuarterPickerInput'
import { DashboardEditorStore } from '../state'
import { PeriodType, SubjectType } from '../types'

/**
 * Zod fragment for the per-widget context fields. Spread into a widget's
 * schema with `...ContextSchemaFields` (subject + period flavor) or
 * `...SubjectSchemaFields` (subject only).
 */
export const SubjectSchemaFields = {
  subject_type: z.enum(['user', 'team', 'overall']).default('overall'),
  subject_id: z.number().default(0),
}

export const PeriodSchemaFields = {
  period_type: z
    .enum(['round', 'month', 'quarter', 'year', 'total'])
    .default('total'),
  period: z.string().default(''),
  use_max_period: z.boolean().default(false),
}

export const ContextSchemaFields = {
  ...SubjectSchemaFields,
  ...PeriodSchemaFields,
}

/**
 * Zod fragment for the colour toggle shared by every widget that applies
 * metric threshold colours. Spread into a widget's schema with
 * `...ColorSchemaFields`.
 */
export const ColorSchemaFields = {
  use_neutral_colors: z.boolean().default(false),
}

/** Default accent used when a widget is set to "neutral colours". */
export const NEUTRAL_COLOR = 'var(--mantine-color-teal-5)'

const SUBJECT_TYPE_OPTIONS = [
  { value: 'overall', label: 'Overall' },
  { value: 'user', label: 'User' },
  { value: 'team', label: 'Team' },
]

const PERIOD_TYPE_OPTIONS = [
  { value: 'total', label: 'Total' },
  { value: 'year', label: 'Year' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'month', label: 'Month' },
  { value: 'round', label: 'Round' },
]

export const COMPONENT_OPTIONS = [
  { value: 'actual', label: 'Actual' },
  { value: 'target', label: 'Target' },
  { value: 'percent', label: 'Percent' },
  { value: 'score', label: 'Score' },
]

export const STATISTIC_OPTIONS = [
  { value: 'value', label: 'Value' },
  { value: 'value_ytd', label: 'Value YTD' },
  { value: 'rank', label: 'Rank' },
  { value: 'team_rank', label: 'Team Rank' },
]

export const SOURCE_OPTIONS = [
  { value: 'all_kpis', label: 'All KPI metrics' },
  { value: 'metric_keys', label: 'Specific metrics' },
]

function MetricSelectInner({
  label,
  value,
  onChange,
  kpiOnly,
  placeholder,
}: {
  label?: string
  value: string
  onChange: (val: string) => void
  kpiOnly?: boolean
  placeholder?: string
}) {
  const { data: metrics } = useMetrics()
  const filtered = kpiOnly ? metrics.filter((m) => m.is_kpi) : metrics
  const data = filtered.map((m) => ({
    value: m.key,
    label: m.name || m.key,
  }))
  return (
    <Select
      label={label ?? 'Metric'}
      placeholder={placeholder ?? 'Pick a metric'}
      data={data}
      value={value || null}
      onChange={(v) => onChange(v || '')}
      searchable
      clearable
      nothingFoundMessage="No metrics"
    />
  )
}

export function MetricSelect(props: {
  label?: string
  value: string
  onChange: (val: string) => void
  kpiOnly?: boolean
  placeholder?: string
}) {
  return (
    <Suspense
      fallback={
        <Select
          label={props.label ?? 'Metric'}
          data={[]}
          disabled
          placeholder="Loading..."
        />
      }
    >
      <MetricSelectInner {...props} />
    </Suspense>
  )
}

export function ComponentSelect({
  value,
  onChange,
  allowed,
}: {
  value: string
  onChange: (val: string) => void
  allowed?: string[]
}) {
  const data = allowed
    ? COMPONENT_OPTIONS.filter((o) => allowed.includes(o.value))
    : COMPONENT_OPTIONS
  return (
    <Select
      label="Component"
      data={data}
      value={value}
      onChange={(v) => onChange(v || 'percent')}
    />
  )
}

export function StatisticSelect({
  value,
  onChange,
  allowed,
}: {
  value: string
  onChange: (val: string) => void
  allowed?: string[]
}) {
  const data = allowed
    ? STATISTIC_OPTIONS.filter((o) => allowed.includes(o.value))
    : STATISTIC_OPTIONS
  return (
    <Select
      label="Statistic"
      data={data}
      value={value}
      onChange={(v) => onChange(v || 'value')}
    />
  )
}

/**
 * Renders Component + Statistic selects together. When `compatibility` is
 * provided, components not listed are hidden, statistics are filtered to
 * those allowed for the current component, and changing the component
 * snaps the statistic to a valid one when needed.
 */
export function ComponentStatisticFields({
  component,
  statistic,
  compatibility,
  onChange,
}: {
  component: string
  statistic: string
  compatibility?: Record<string, string[]>
  onChange: (patch: { component?: string; statistic?: string }) => void
}) {
  const allowedComponents = compatibility
    ? Object.keys(compatibility)
    : undefined
  const allowedStatistics = compatibility
    ? (compatibility[component] ?? [])
    : undefined

  const handleComponentChange = (next: string) => {
    if (!compatibility) {
      onChange({ component: next })
      return
    }
    const nextAllowed = compatibility[next] ?? []
    const nextStat = nextAllowed.includes(statistic)
      ? statistic
      : nextAllowed[0]
    onChange({ component: next, statistic: nextStat })
  }

  return (
    <>
      <ComponentSelect
        allowed={allowedComponents}
        value={component}
        onChange={handleComponentChange}
      />
      <StatisticSelect
        allowed={allowedStatistics}
        value={statistic}
        onChange={(v) => onChange({ statistic: v })}
      />
    </>
  )
}

export function TitleInput({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string
  onChange: (val: string) => void
  label?: string
  placeholder?: string
}) {
  return (
    <TextInput
      label={label ?? 'Title'}
      placeholder={placeholder ?? 'Optional title'}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  )
}

export function KpiSourceFields({
  source,
  metric_keys,
  onChange,
}: {
  source: string
  metric_keys: string[]
  onChange: (patch: { source?: string; metric_keys?: string[] }) => void
}) {
  return (
    <Stack gap="xs">
      <Select
        label="Source"
        data={SOURCE_OPTIONS}
        value={source}
        onChange={(v) => onChange({ source: v || 'all_kpis' })}
      />
      {source === 'metric_keys' && (
        <MultiMetricSelect
          value={metric_keys}
          onChange={(v) => onChange({ metric_keys: v })}
        />
      )}
    </Stack>
  )
}

function MultiMetricSelectInner({
  value,
  onChange,
}: {
  value: string[]
  onChange: (val: string[]) => void
}) {
  const { data: metrics } = useMetrics()
  const data = metrics.map((m) => ({ value: m.key, label: m.name || m.key }))
  return (
    <Select
      label="Metrics"
      placeholder="Pick at least one"
      data={data}
      value={null}
      onChange={(v) => {
        if (!v) return
        if (value.includes(v)) return
        onChange([...value, v])
      }}
      searchable
      nothingFoundMessage="No metrics"
      description={
        value.length > 0
          ? `Selected: ${value.join(', ')}`
          : 'Pick metrics to add'
      }
    />
  )
}

function MultiMetricSelect(props: {
  value: string[]
  onChange: (val: string[]) => void
}) {
  return (
    <Suspense
      fallback={
        <Select label="Metrics" data={[]} disabled placeholder="Loading..." />
      }
    >
      <MultiMetricSelectInner {...props} />
    </Suspense>
  )
}

export function PeriodOverrideToggle({
  use_max_period,
  onChange,
}: {
  use_max_period: boolean
  onChange: (val: boolean) => void
}) {
  return (
    <Switch
      label="Use latest period with data"
      checked={use_max_period}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  )
}

export function NeutralColorsToggle({
  value,
  onChange,
}: {
  value: boolean
  onChange: (val: boolean) => void
}) {
  return (
    <Switch
      label="Use neutral colors"
      checked={value}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  )
}

function UserSelectInner({
  value,
  onChange,
  competitionId,
}: {
  value: number
  onChange: (val: number) => void
  competitionId: number
}) {
  const { data } = useAllPlayers({ competition_id: competitionId })
  const options = data.items
    .filter((p) => p.user_id != null)
    .map((p) => ({
      value: String(p.user_id),
      label: `${p.name} — ${p.team_name}`,
    }))
  return (
    <Select
      label="User"
      size="xs"
      data={options}
      value={value > 0 ? String(value) : null}
      onChange={(v) => onChange(v ? parseInt(v, 10) : 0)}
      searchable
      clearable
      placeholder="Pick a user"
      nothingFoundMessage="No users in competition"
    />
  )
}

function TeamSelectInner({
  value,
  onChange,
  competitionId,
}: {
  value: number
  onChange: (val: number) => void
  competitionId: number
}) {
  const { data } = useAllTeams({ competition_id: competitionId })
  const options = data.items.map((t) => ({
    value: String(t.team_id),
    label: t.name,
  }))
  return (
    <Select
      label="Team"
      size="xs"
      data={options}
      value={value > 0 ? String(value) : null}
      onChange={(v) => onChange(v ? parseInt(v, 10) : 0)}
      searchable
      clearable
      placeholder="Pick a team"
      nothingFoundMessage="No teams in competition"
    />
  )
}

export function SubjectFields({
  subject_type,
  subject_id,
  subjectTypes,
  onChange,
}: {
  subject_type: SubjectType
  subject_id: number
  subjectTypes?: SubjectType[]
  onChange: (patch: { subject_type?: SubjectType; subject_id?: number }) => void
}) {
  const competitionId = DashboardEditorStore.useState((s) => s.competitionId)

  const typeOptions = subjectTypes
    ? SUBJECT_TYPE_OPTIONS.filter((o) =>
        subjectTypes.includes(o.value as SubjectType)
      )
    : SUBJECT_TYPE_OPTIONS

  const subjectPicker = (() => {
    if (subject_type === 'overall') {
      return (
        <Select
          label="Subject value"
          size="xs"
          data={[]}
          value={null}
          disabled
          placeholder="N/A"
        />
      )
    }
    if (!competitionId) {
      return (
        <Select
          label={subject_type === 'user' ? 'User' : 'Team'}
          size="xs"
          data={[]}
          disabled
          placeholder="Set a competition first"
        />
      )
    }
    const fallback = (
      <Select
        label={subject_type === 'user' ? 'User' : 'Team'}
        size="xs"
        data={[]}
        disabled
        placeholder="Loading..."
      />
    )
    return (
      <Suspense fallback={fallback}>
        {subject_type === 'user' ? (
          <UserSelectInner
            value={subject_id}
            onChange={(v) => onChange({ subject_id: v })}
            competitionId={competitionId}
          />
        ) : (
          <TeamSelectInner
            value={subject_id}
            onChange={(v) => onChange({ subject_id: v })}
            competitionId={competitionId}
          />
        )}
      </Suspense>
    )
  })()

  return (
    <Group gap="xs" align="end" grow>
      <Select
        label="Subject"
        size="xs"
        data={typeOptions}
        value={subject_type}
        onChange={(v) =>
          onChange({
            subject_type:
              (v as SubjectType) || (subjectTypes?.[0] ?? 'overall'),
            subject_id: 0,
          })
        }
      />
      {subjectPicker}
    </Group>
  )
}

function normalizeYear(v: string | null): string {
  if (!v) return ''
  return v.slice(0, 4) + '-01-01'
}

function normalizeMonth(v: string | null): string {
  if (!v) return ''
  return v.slice(0, 7) + '-01'
}

export function PeriodFields({
  period_type,
  period,
  use_max_period,
  onChange,
}: {
  period_type: PeriodType
  period: string
  use_max_period: boolean
  onChange: (patch: {
    period_type?: PeriodType
    period?: string
    use_max_period?: boolean
  }) => void
}) {
  const periodPicker = (() => {
    if (period_type === 'total') {
      return (
        <TextInput
          label="Period"
          size="xs"
          value=""
          disabled
          placeholder="N/A"
        />
      )
    }
    if (period_type === 'year') {
      return (
        <YearPickerInput
          label="Period"
          size="xs"
          valueFormat="YYYY"
          placeholder="Pick a year"
          value={period || null}
          onChange={(v) => onChange({ period: normalizeYear(v) })}
          disabled={use_max_period}
          clearable
        />
      )
    }
    if (period_type === 'month') {
      return (
        <MonthPickerInput
          label="Period"
          size="xs"
          valueFormat="MMM YYYY"
          placeholder="Pick a month"
          value={period || null}
          onChange={(v) => onChange({ period: normalizeMonth(v) })}
          disabled={use_max_period}
          clearable
        />
      )
    }
    if (period_type === 'quarter') {
      return (
        <QuarterPickerInput
          size="xs"
          value={period || null}
          onChange={(v) => onChange({ period: v })}
          disabled={use_max_period}
        />
      )
    }
    // round
    return (
      <DatePickerInput
        label="Period"
        size="xs"
        placeholder="Pick a round date"
        value={period || null}
        onChange={(v) => onChange({ period: v ?? '' })}
        disabled={use_max_period}
        clearable
      />
    )
  })()

  return (
    <Stack gap="xs">
      <Select
        label="Period type"
        size="xs"
        data={PERIOD_TYPE_OPTIONS}
        value={period_type}
        onChange={(v) =>
          onChange({
            period_type: (v as PeriodType) || 'total',
            period: '',
          })
        }
      />
      {periodPicker}
      <Switch
        label="Use latest period with data"
        size="xs"
        checked={use_max_period}
        onChange={(e) => onChange({ use_max_period: e.currentTarget.checked })}
      />
    </Stack>
  )
}

export function ContextFields(props: {
  subject_type: SubjectType
  subject_id: number
  period_type: PeriodType
  period: string
  use_max_period: boolean
  onChange: (patch: {
    subject_type?: SubjectType
    subject_id?: number
    period_type?: PeriodType
    period?: string
    use_max_period?: boolean
  }) => void
}) {
  return (
    <Stack gap="xs">
      <SubjectFields
        subject_type={props.subject_type}
        subject_id={props.subject_id}
        onChange={props.onChange}
      />
      <PeriodFields
        period_type={props.period_type}
        period={props.period}
        use_max_period={props.use_max_period}
        onChange={props.onChange}
      />
    </Stack>
  )
}

function DivisionSelectInner({
  value,
  onChange,
  competitionId,
}: {
  value: number | null
  onChange: (val: number | null) => void
  competitionId: number
}) {
  const { data: divisions } = useDivisions({ competition_id: competitionId })
  const data = useMemo(
    () => [
      { value: '', label: 'All divisions' },
      ...divisions.map((d) => ({
        value: String(d.division_id),
        label: d.name,
      })),
    ],
    [divisions]
  )
  return (
    <Select
      label="Division"
      data={data}
      value={value == null ? '' : String(value)}
      onChange={(v) => onChange(v ? Number(v) : null)}
    />
  )
}

export function DivisionSelect({
  value,
  onChange,
}: {
  value: number | null
  onChange: (val: number | null) => void
}) {
  const competitionId = DashboardEditorStore.useState((s) => s.competitionId)

  if (!competitionId) {
    return (
      <Select
        label="Division"
        data={[]}
        disabled
        placeholder="Set a competition first"
      />
    )
  }

  return (
    <Suspense
      fallback={
        <Select label="Division" data={[]} disabled placeholder="Loading..." />
      }
    >
      <DivisionSelectInner
        value={value}
        onChange={onChange}
        competitionId={competitionId}
      />
    </Suspense>
  )
}

function RoundSelectInner({
  value,
  onChange,
  competitionId,
}: {
  value: number | null
  onChange: (val: number | null) => void
  competitionId: number
}) {
  const { data: rounds } = useCompetitionRounds({
    competition_id: competitionId,
  })
  const data = useMemo(
    () =>
      rounds.map((r) => ({
        value: String(r.round_number),
        label: `Round ${r.round_number}`,
      })),
    [rounds]
  )
  return (
    <Select
      label="Round"
      placeholder="Pick a round"
      data={data}
      value={value == null ? null : String(value)}
      onChange={(v) => onChange(v ? Number(v) : null)}
    />
  )
}

export function RoundSelect({
  value,
  onChange,
}: {
  value: number | null
  onChange: (val: number | null) => void
}) {
  const competitionId = DashboardEditorStore.useState((s) => s.competitionId)

  if (!competitionId) {
    return (
      <Select
        label="Round"
        data={[]}
        disabled
        placeholder="Set a competition first"
      />
    )
  }

  return (
    <Suspense
      fallback={
        <Select label="Round" data={[]} disabled placeholder="Loading..." />
      }
    >
      <RoundSelectInner
        value={value}
        onChange={onChange}
        competitionId={competitionId}
      />
    </Suspense>
  )
}

function MatchSelectInner({
  value,
  onChange,
  competitionId,
}: {
  value: number | null
  onChange: (val: number | null) => void
  competitionId: number
}) {
  const { data: matches } = useMatches({ competition_id: competitionId })
  const data = useMemo(
    () =>
      [...matches]
        .sort(
          (a, b) => a.round_number - b.round_number || a.match_id - b.match_id
        )
        .map((m) => ({
          value: String(m.match_id),
          label: `Round ${m.round_number}: ${m.home_team_name} vs ${
            m.away_team_id == null ? 'BYE' : m.away_team_name
          }`,
        })),
    [matches]
  )
  return (
    <Select
      label="Match"
      placeholder="Pick a match"
      searchable
      data={data}
      value={value == null ? null : String(value)}
      onChange={(v) => onChange(v ? Number(v) : null)}
    />
  )
}

export function MatchSelect({
  value,
  onChange,
}: {
  value: number | null
  onChange: (val: number | null) => void
}) {
  const competitionId = DashboardEditorStore.useState((s) => s.competitionId)

  if (!competitionId) {
    return (
      <Select
        label="Match"
        data={[]}
        disabled
        placeholder="Set a competition first"
      />
    )
  }

  return (
    <Suspense
      fallback={
        <Select label="Match" data={[]} disabled placeholder="Loading..." />
      }
    >
      <MatchSelectInner
        value={value}
        onChange={onChange}
        competitionId={competitionId}
      />
    </Suspense>
  )
}
